import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import PaymentModal from "@/components/PaymentModal";
import AstroBackground from "@/components/AstroBackground";
import useSEO from "@/hooks/useSEO";
import { ASTROLOGY_SERVICES, WHATSAPP_URL } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const SERVICES = ASTROLOGY_SERVICES.filter(s =>
  ["numerology-online", "numerology-inperson"].includes(s.id)
);

const WHAT_YOU_RECEIVE = [
  "Life Path, Expression, and Soul Urge number analysis",
  "Personal Year forecast for the current and next cycle",
  "Name correction recommendations and vibrational assessment",
  "Business name or brand name analysis (if applicable)",
  "Relationship and partner compatibility through numerology",
  "Identification of lucky and challenging number cycles",
];

const WHO_ITS_FOR = [
  { label: "Name Corrections", desc: "Individuals considering name changes for personal or professional branding." },
  { label: "Business Owners", desc: "Evaluating company names, brand identities, or product naming for energetic alignment." },
  { label: "Recurring Patterns", desc: "Seeking clarity on why certain life patterns, obstacles, or opportunities repeat." },
];

function PriceRow({ service, onBook }) {
  return (
    <div data-testid={`num-price-row-${service.id}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0", borderBottom: `1px solid ${BORDER}`, gap: "16px", flexWrap: "wrap" }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontWeight: 500, color: CHARCOAL }}>{service.name}</p>
        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.78rem", color: "#8A8A8A", marginTop: "4px" }}>{service.mode} · {service.location}</p>
      </div>
      <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.05rem", fontWeight: 600, color: CHARCOAL, whiteSpace: "nowrap" }}>
        ₹{service.price.toLocaleString("en-IN")}
      </p>
      <button
        onClick={() => onBook(service)}
        data-testid={`num-book-btn-${service.id}`}
        style={{ backgroundColor: CHARCOAL, color: OFF_WHITE, padding: "10px 20px", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", border: "none", cursor: "pointer", whiteSpace: "nowrap", transition: "background-color 0.3s" }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = GOLD; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = CHARCOAL; }}
      >
        Book &amp; Pay
      </button>
    </div>
  );
}

export default function NumerologyPage() {
  const [selectedService, setSelectedService] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const handleBook = (s) => { setSelectedService(s); setModalOpen(true); };
  useSEO({
    title: "Numerology Consultation | Akkshay Narula — Chaldean Numerologist India",
    description: "Personal numerology readings by Akkshay Narula using the Chaldean system — name correction, Life Path analysis, business name evaluation. Online and in-person from ₹3,100.",
    keywords: "numerology consultation India, chaldean numerology India, name correction numerology, numerologist Bangalore, numerologist Guwahati, name change numerology online",
    canonical: "/services/numerology",
  });

  return (
    <main style={{ backgroundColor: OFF_WHITE }} data-testid="numerology-page">
      {/* Hero */}
      <section style={{ backgroundColor: CHARCOAL, padding: "80px 0", position: "relative", overflow: "hidden" }}>
        <AstroBackground count={12} />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16" style={{ position: "relative", zIndex: 1 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#6A6A6A", fontFamily: "'Outfit', sans-serif", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase", textDecoration: "none", marginBottom: "36px" }}>
            <ArrowLeft size={11} /> Home
          </Link>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>Name & Numbers</p>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "20px" }}>
            <span style={{ fontFamily: "serif", fontSize: "4.5rem", color: GOLD, opacity: 0.22, lineHeight: 1, flexShrink: 0, marginTop: "6px" }}>☿</span>
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, color: OFF_WHITE, lineHeight: 1.1, marginBottom: "16px" }}>
                Numerology
              </h1>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#8A8A8A", lineHeight: 1.75, maxWidth: "540px" }}>
                Unlock the vibrational code of your name and birth date — for direction, purpose, and precise life timing.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "40px", marginTop: "40px", flexWrap: "wrap" }}>
            {[{ label: "Format", value: "Online & In-Person" }, { label: "System", value: "Chaldean Numerology" }, { label: "Starting From", value: "₹3,100" }].map(item => (
              <div key={item.label}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.58rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#4A4A4A", marginBottom: "5px" }}>{item.label}</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: OFF_WHITE, fontWeight: 500 }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section style={{ padding: "80px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>About This Service</p>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "24px" }}>What is Numerology?</h2>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.85, marginBottom: "18px" }}>
                Numerology is the science of numbers and their resonance with human energy and life events. Akkshay Narula practises Chaldean numerology — one of the oldest and most accurate systems — analysing the vibrational value of your full name and date of birth to reveal your Life Path, Expression, Soul Urge, and Personality numbers.
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", lineHeight: 1.85 }}>
                Whether you're evaluating a name change, naming a business, or trying to understand why certain patterns repeat — a numerology consultation delivers precise, actionable answers grounded in your unique numerical blueprint.
              </p>
            </div>
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>What You'll Receive</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
                {WHAT_YOU_RECEIVE.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start", fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.65 }}>
                    <span style={{ color: GOLD, fontFamily: "serif", fontSize: "0.9rem", lineHeight: 1.75, flexShrink: 0 }}>—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section style={{ padding: "72px 0", backgroundColor: "#F5F3EB", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Ideal For</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>Who Is This For</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0" style={{ border: `1px solid ${BORDER}` }}>
            {WHO_ITS_FOR.map((item, i) => (
              <div key={i} style={{ padding: "32px", borderRight: i < 2 ? `1px solid ${BORDER}` : "none" }}>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 500, color: CHARCOAL, marginBottom: "10px" }}>{item.label}</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "#4A4A4A", lineHeight: 1.75 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: "80px 0" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>Fees</p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3vw, 2.4rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "8px" }}>Consultation Fees</h2>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "#8A8A8A", marginBottom: "40px" }}>100% advance payment. All fees are fixed and non-negotiable.</p>
          {SERVICES.map(s => <PriceRow key={s.id} service={s} onBook={handleBook} />)}
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: CHARCOAL, padding: "72px 0", position: "relative", overflow: "hidden" }}>
        <AstroBackground count={8} />
        <div className="max-w-2xl mx-auto px-6 text-center" style={{ position: "relative", zIndex: 1 }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 500, color: OFF_WHITE, marginBottom: "16px" }}>Ready to Begin?</h2>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#8A8A8A", marginBottom: "32px", lineHeight: 1.7 }}>Select a service above to book, or message on WhatsApp to discuss your requirements first.</p>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
            style={{ backgroundColor: GOLD, color: CHARCOAL, padding: "15px 36px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "8px" }}>
            Request via WhatsApp <ArrowRight size={13} />
          </a>
        </div>
      </section>

      <PaymentModal service={selectedService} open={modalOpen} onClose={() => setModalOpen(false)} />
    </main>
  );
}
